import React from "react";
import {
  Box,
  Button,
  Container,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const paper = "#faf8f4";
const ink = "#2c4054";
const inkMuted = "#5a6e82";
const accent = "#4a7eb0";
const accentHover = "#3d6a9a";
const footerBg = "#2a3f56";

const LOGO_WHITE = `${process.env.PUBLIC_URL || ""}/white_on_trans.png`;

/** Public asset — filename contains a space. */
const HERO_PHOTO = `${process.env.PUBLIC_URL || ""}/onthe%20farm.png`;

const heroScrim =
  "linear-gradient(to bottom, rgba(26, 42, 58, 0.55) 0%, rgba(26, 42, 58, 0.28) 42%, rgba(26, 42, 58, 0.5) 100%), " +
  `linear-gradient(135deg, rgba(74, 126, 176, 0.12) 0%, transparent 55%)`;

const features = [
  {
    title: "Animals",
    body: "Keep your herd register, profiles, and history together so nothing lives on scraps of paper.",
  },
  {
    title: "Treatments",
    body: "Log health events and care over time so you and your vet see the full picture.",
  },
  {
    title: "Medicine cabinet",
    body: "Track stock, batches, and usage so you know what is on hand before you need it.",
  },
  {
    title: "Milking",
    body: "A production-oriented dashboard for teams that track milk where your access allows.",
  },
  {
    title: "Stock taking",
    body: "Run herd attendance and checks when you need a confident count against the register.",
  },
  {
    title: "Activities",
    body: "Plan and record farm activities in one list so jobs do not slip between shifts.",
  },
];

export default function LandingPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: paper,
        color: ink,
        fontFamily: (t) => t.typography.fontFamily,
      }}
    >
      {/* Panoramic hero — image + scrim; header and headline over photo (Livestocked-style). */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "min(92vh, 720px)", md: "min(88vh, 820px)" },
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#1a2a38",
          backgroundImage: `${heroScrim}, url(${JSON.stringify(HERO_PHOTO)})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "66% center", md: "center center" },
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          component="header"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            flexShrink: 0,
            borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            bgcolor: "rgba(26, 42, 58, 0.35)",
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: { xs: 1.25, sm: 1.5 } }}
            >
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                  filter: "drop-shadow(0 1px 8px rgba(0,0,0,0.35))",
                  "&:hover": { opacity: 0.92 },
                }}
              >
                <Box
                  component="img"
                  src={LOGO_WHITE}
                  alt="OnTheFarm Today"
                  sx={{
                    height: { xs: 52, sm: 64 },
                    width: "auto",
                    display: "block",
                  }}
                />
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 2 }}
                sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}
              >
                <MuiLink
                  href="#features"
                  underline="hover"
                  sx={{
                    color: "rgba(255,255,255,0.88)",
                    fontSize: "0.9rem",
                    display: { xs: "none", sm: "inline" },
                    textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                  }}
                >
                  Features
                </MuiLink>
                <MuiLink
                  href="#pricing"
                  underline="hover"
                  sx={{
                    color: "rgba(255,255,255,0.88)",
                    fontSize: "0.9rem",
                    display: { xs: "none", sm: "inline" },
                    textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                  }}
                >
                  Pricing
                </MuiLink>
                <Button
                  component={RouterLink}
                  to="/login"
                  color="inherit"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    textShadow: "0 1px 6px rgba(0,0,0,0.35)",
                  }}
                >
                  Log in
                </Button>
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: footerBg,
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                    "&:hover": { bgcolor: "#1f3244", boxShadow: "0 6px 18px rgba(0,0,0,0.3)" },
                  }}
                >
                  Sign up
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pt: { xs: 4, md: 6 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.65rem" },
                color: "#fff",
                mb: 2,
                textShadow: "0 2px 24px rgba(0,0,0,0.45)",
              }}
            >
              Your herd, your records, one calm place online
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontSize: { xs: "1.02rem", md: "1.12rem" },
                lineHeight: 1.65,
                maxWidth: 560,
                mx: "auto",
                mb: 4,
                textShadow: "0 1px 16px rgba(0,0,0,0.4)",
              }}
            >
              OnTheFarm Today is a web-based workspace for daily farm operations—animals, health,
              medicines, milk, counts, and simple planning—without installing another app store
              download.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
              <Button
                component={RouterLink}
                to="/signup"
                variant="contained"
                size="large"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  bgcolor: accent,
                  color: "#fff",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                  "&:hover": { bgcolor: accentHover, boxShadow: "0 8px 24px rgba(0,0,0,0.28)" },
                }}
              >
                Create an account
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                size="large"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  borderColor: "rgba(255,255,255,0.85)",
                  color: "#fff",
                  borderWidth: 2,
                  "&:hover": {
                    borderColor: "#fff",
                    borderWidth: 2,
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                Log in
              </Button>
            </Stack>
          </Container>
        </Box>

      </Box>

      <Container id="features" maxWidth="lg" sx={{ scrollMarginTop: 96, py: { xs: 7, md: 9 } }}>
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1, color: ink }}
        >
          Features
        </Typography>
        <Typography align="center" sx={{ color: inkMuted, mb: 5, maxWidth: 520, mx: "auto" }}>
          Built for low noise: each area does one job clearly, aligned with what the product offers
          today.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: { md: 8 },
            rowGap: 3.5,
          }}
        >
          {features.map((f) => (
            <Box key={f.title}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: ink, mb: 0.75 }}>
                {f.title}
              </Typography>
              <Typography sx={{ color: inkMuted, lineHeight: 1.65, fontSize: "0.95rem" }}>
                {f.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 7, md: 9 } }}>
        <Box
          id="pricing"
          sx={{
            scrollMarginTop: 96,
            bgcolor: "rgba(255,255,255,0.88)",
            border: "1px solid rgba(44, 64, 84, 0.1)",
            borderRadius: 3,
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: "center",
            boxShadow: "0 8px 28px rgba(44, 64, 84, 0.06)",
          }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: ink, mb: 1 }}>
            Pricing
          </Typography>
          <Typography sx={{ color: inkMuted, lineHeight: 1.7, mb: 3 }}>
            We are working on simple, herd-sized plans you can understand at a glance. Until those
            are published, you can still sign up and use the product—we will give plenty of notice
            before anything changes.
          </Typography>
          <Button
            component={RouterLink}
            to="/signup"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: accent,
              px: 3,
              boxShadow: "none",
              "&:hover": { bgcolor: accentHover, boxShadow: "none" },
            }}
          >
            Get started
          </Button>
        </Box>
      </Container>

      <Box
        component="footer"
        sx={{
          bgcolor: footerBg,
          color: "rgba(255,255,255,0.82)",
          py: 5,
          mt: "auto",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                opacity: 0.92,
                "&:hover": { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={LOGO_WHITE}
                alt="OnTheFarm Today"
                sx={{ height: { xs: 46, sm: 56 }, width: "auto", display: "block" }}
              />
            </Box>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <MuiLink component={RouterLink} to="/signup" underline="hover" sx={{ color: "inherit" }}>
                Sign up
              </MuiLink>
              <MuiLink component={RouterLink} to="/login" underline="hover" sx={{ color: "inherit" }}>
                Log in
              </MuiLink>
              <MuiLink href="#features" underline="hover" sx={{ color: "inherit" }}>
                Features
              </MuiLink>
              <MuiLink href="#pricing" underline="hover" sx={{ color: "inherit" }}>
                Pricing
              </MuiLink>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
